import { type Dispatch, type FC, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Download, Search } from "lucide-react";
import {
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  DefinitionTooltip,
  TablePagePagination,
  dashboardDataTable as dt,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  PAID_MEDIA_TAG_ROLE_ADSET,
  PAID_MEDIA_TAG_ROLE_CAMPAIGN,
  googleAdsAdNetworkChannelClass,
} from "../../utils/PaidMediaTags";
import {
  getGoogleColumnTooltip,
  getRoasToneClass,
  type AdGroupRow,
  type GoogleAdsAdGroupSortKey,
  type GoogleSectionHelpKey,
} from "./GoogleAdsTableConfig";
import { formatPaidMediaRoas } from "../../utils/PaidMediaNumberFormat";

interface GoogleAdGroupsTableProps {
  googleSectionHelp: (_key: GoogleSectionHelpKey) => DefinitionTooltipInfo;
  adGroupSearch: string;
  setAdGroupSearch: Dispatch<SetStateAction<string>>;
  setCurrentAdGroupPage: Dispatch<SetStateAction<number>>;
  isExporting: boolean;
  exportAdGroupsCsv: () => void;
  toggleAdGroupSort: (_key: GoogleAdsAdGroupSortKey) => void;
  adGroupRows: AdGroupRow[];
  formatCountMetric: (_value: number | null | undefined) => string;
  formatCurrency: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  formatNullablePercent: (_value: number | null | undefined) => string;
  formatCurrencyShort: (_value: number) => string;
  formatConversionMetric: (_value: number | null | undefined) => string;
  currentAdGroupPage: number;
  adGroupTotalPages: number;
}

const GoogleAdGroupsTable: FC<GoogleAdGroupsTableProps> = ({
  googleSectionHelp,
  adGroupSearch,
  setAdGroupSearch,
  setCurrentAdGroupPage,
  isExporting,
  exportAdGroupsCsv,
  toggleAdGroupSort,
  adGroupRows,
  formatCountMetric,
  formatCurrency,
  formatNullablePercent,
  formatCurrencyShort,
  formatConversionMetric,
  currentAdGroupPage,
  adGroupTotalPages,
}) => {
  const { t } = useTranslation();

  return (
    <DashboardDataTablePanel className="google-ads-table-panel--full">
      <DashboardDataTablePanelHeader>
        <div className="paid-media-panel__heading">
          <h3>{t("googleAdsReport.sections.adGroupPerformance")}</h3>
          <DefinitionTooltip
            label={t("googleAdsReport.sections.adGroupPerformance")}
            info={googleSectionHelp("adGroupPerformance")}
            formulaTitle={t("webAnalytics.kpi.formula")}
          />
        </div>
        <div className="paid-media-table-toolbar" role="toolbar" aria-label={t("googleAdsReport.toolbars.adGroupSearch")}>
          <label className={`${dt.search} paid-media-table-toolbar__search`}>
            <Search size={16} />
            <input
              type="search"
              value={adGroupSearch}
              placeholder={t("googleAdsReport.placeholders.searchAdGroups")}
              onChange={event => {
                setAdGroupSearch(event.target.value);
                setCurrentAdGroupPage(1);
              }}
            />
          </label>
          <button
            type="button"
            className="paid-media-export-btn"
            disabled={isExporting}
            onClick={() => void exportAdGroupsCsv()}
          >
            <Download size={16} aria-hidden />
            {isExporting ? t("googleAdsReport.export.exporting") : t("googleAdsReport.export.csv")}
          </button>
        </div>
      </DashboardDataTablePanelHeader>
      <DashboardDataTableScroll>
        <DashboardDataTableTable className="google-ads-adgroup-table">
          <thead>
            <tr>
              <th>
                <DashboardDataTableSortButton onClick={() => toggleAdGroupSort("name")}>
                  {t("googleAdsReport.adGroupTable.adGroup")}
                </DashboardDataTableSortButton>
              </th>
              {(
                [
                  "interactions",
                  "spend",
                  "impressions",
                  "clicks",
                  "ctr",
                  "cpc",
                  "conversions",
                  "cpa",
                  "roas",
                ] as const
              ).map((column, columnIndex, columns) => (
                <th key={column} className={dt.number}>
                  <span className={dt.thHelpRow}>
                    <DashboardDataTableSortButton
                      onClick={() => toggleAdGroupSort(column)}
                    >
                      {t(`googleAdsReport.adGroupTable.${column}`)}
                    </DashboardDataTableSortButton>
                    <DefinitionTooltip
                      label={t(`googleAdsReport.adGroupTable.${column}`)}
                      info={getGoogleColumnTooltip(t, column, "adGroup")}
                      align={columnIndex >= columns.length - 3 ? "end" : "start"}
                      formulaTitle={t("webAnalytics.kpi.formula")}
                    />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {adGroupRows.map(row => (
              <tr key={row.id}>
                <td>
                  <div className="paid-media-campaign-cell">
                    <strong>{row.name}</strong>
                    <div className="paid-media-tag-row">
                      <span className={`paid-media-mini-tag ${PAID_MEDIA_TAG_ROLE_ADSET}`}>
                        {t("googleAdsReport.adGroupRowTags.adGroup")}
                      </span>
                      {row.adNetworkType ? (
                        <span
                          className={`paid-media-mini-tag ${googleAdsAdNetworkChannelClass(row.adNetworkType)}`}
                        >
                          {row.adNetworkType}
                        </span>
                      ) : null}
                      <span className={`paid-media-mini-tag ${PAID_MEDIA_TAG_ROLE_CAMPAIGN}`}>
                        {t("googleAdsReport.adGroupRowTags.multiCampaign")}
                      </span>
                    </div>
                  </div>
                </td>
                <td className={dt.number}>{formatCountMetric(row.interactions)}</td>
                <td className={dt.number}>{formatCurrency(row.spend)}</td>
                <td className={dt.number}>{formatCountMetric(row.impressions)}</td>
                <td className={dt.number}>{formatCountMetric(row.clicks)}</td>
                <td className={dt.number}>{formatNullablePercent(row.ctr)}</td>
                <td className={dt.number}>{formatCurrencyShort(row.cpc)}</td>
                <td className={dt.number}>{formatConversionMetric(row.conversions)}</td>
                <td className={dt.number}>{formatCurrencyShort(row.cpa)}</td>
                <td className={`${dt.number} ${getRoasToneClass(row.roas)}`}>{formatPaidMediaRoas(row.roas)}</td>
              </tr>
            ))}
          </tbody>
        </DashboardDataTableTable>
      </DashboardDataTableScroll>
      <TablePagePagination
        currentPage={currentAdGroupPage}
        totalPages={adGroupTotalPages}
        onPrevious={() => setCurrentAdGroupPage(page => Math.max(1, page - 1))}
        onNext={() =>
          setCurrentAdGroupPage(page => Math.min(adGroupTotalPages, page + 1))
        }
      />
    </DashboardDataTablePanel>
  );
};

export default GoogleAdGroupsTable;
